          
<script setup>
import { ref, onUnmounted, watch, watchEffect, computed } from 'vue';
import { useMainStore } from '../stores/store.js';
import { callAPI, DRUPAL_API_ROUTE } from "@/service/DrupalService";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';
import { parseDomain, fromUrl } from "parse-domain";

const { t } = useI18n();

const props = defineProps(['visible']);
const emit = defineEmits(['update:visible', 'domain']);

const newDomainNameSchema = yup.object({
  newDomainName: yup.string().required().notOneOf(['http', 'https'])
    .matches(/^(?!.*(http|https):\/\/|www\.)/)
});

const newLCCXSubdomainSchema = yup.object({
  newLCCXSubdomain: yup.string().required().min(4).matches(/^(?![0-9]+$)(?!.*-$)(?!-)[a-zA-Z0-9-]{1,63}$/),
  lccxSubDomain: yup.string().required().oneOf([".lc.cx", ".url.fr", ".short.do"])
});

const currentSchema = ref(newDomainNameSchema);

const { defineField, handleSubmit, resetForm, errors } = useForm({
  validationSchema: computed(() => currentSchema.value),
});

const [newLCCXSubdomain] = defineField('newLCCXSubdomain');
const [newDomainName] = defineField('newDomainName');
const [lccxSubDomain] = defineField('lccxSubDomain');

const currentSchemaHandle = (domainOption) => {
  switch (domainOption) {
    case 'domainOption1':
      currentSchema.value = newDomainNameSchema;
      break;
    case 'domainOption2':
      currentSchema.value = newLCCXSubdomainSchema;
      break;

    default:
      break;
  }
}

const user = ref(JSON.parse(localStorage.getItem('user')));
const addDomainDialog = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
});
const domainOption = ref();
const newDomainDescription = ref();
const handleDomainLoading = ref(false);
const newDomainIsLCCX = ref(false);
const newDomainIsURLFR = ref(false);
const newDomainIsSHORTDO = ref(false);
const activeStep = ref(1);
const confDomainName = ref();
const confDomainSetup = ref(false);
const confDomainSetupLoading = ref(false);
const addDomainDialogHeight = ref('100%');
const domainLookupLoading = ref(false);
const domainSearch = ref();
const domainSearchResults = ref([]);
const domainSearchPricesResults = ref([]);
const lccxSubDomains = ref([{ 'label': '.short.do', 'value': '.short.do' }, { 'label': '.lc.cx', 'value': '.lc.cx' }, { 'label': '.url.fr', 'value': '.url.fr' }]);
const isAllowed = computed(() => user.value.plan.max_domains > user.value.plan.domains_usage);
const cloudProvider = import.meta.env.VITE_CLOUD_PROVIDER;
const domainWorkspaces = ref([]);
const selectedWorkspaces = ref([]);
const domainWorkspacesLoading = ref(false);

// Workspaces where the user may manage domains. The default workspace always
// gets the domain (forced by the API too): selected and locked.
const fetchDomainWorkspaces = async () => {
  domainWorkspacesLoading.value = true;
  var defaultWorkspace = null;

  try {
    var [workspacesResponse, defaultResponse] = await Promise.all([
      callAPI(DRUPAL_API_ROUTE.WORKSPACES),
      callAPI(DRUPAL_API_ROUTE.WORKSPACE_DEFAULT)
    ]);
    defaultWorkspace = defaultResponse.data;
    domainWorkspaces.value = (workspacesResponse.data || [])
      .filter(workspace => workspace.id == defaultWorkspace || workspace.domains_management)
      .map(workspace => ({ ...workspace, locked: workspace.id == defaultWorkspace }));
  } catch (error) {
    console.log(error);
    domainWorkspaces.value = [];
  } finally {
    domainWorkspacesLoading.value = false;
  }

  selectedWorkspaces.value = domainWorkspaces.value
    .filter(workspace => workspace.locked || workspace.id == user.value.current_workspace)
    .map(workspace => workspace.id);
}

// Keep the default workspace selected even if it is unselected.
watch(selectedWorkspaces, (value) => {
  var locked = domainWorkspaces.value.find(workspace => workspace.locked);
  if (locked && !value.includes(locked.id)) selectedWorkspaces.value = [locked.id, ...value];
});

let intervalId = null;

const onSubmitCreationDomain = handleSubmit((values) => {
  handleDomain();
});

const handleDomain = async () => {
  var domain;
  if (newDomainName.value) newDomainName.value = newDomainName.value.toLowerCase();

  // Step 2: domain form, then workspaces choice.
  if (activeStep.value == 2) {
    if (domainOption.value == 'domainOption1') {
      const { subDomains, domain, topLevelDomains } = parseDomain(
        fromUrl('https://' + newDomainName.value),
      );

      if (Array.isArray(subDomains)) {
        if (subDomains.length === 0) {
          confDomainName.value = '@';
        } else {
          confDomainName.value = subDomains.join('.');
        }
      }
    }

    activeStep.value = 3;
    return;
  }

  if (activeStep.value == 3 && domainOption.value == 'domainOption2') {
    handleDomainLoading.value = true;
    newDomainIsLCCX.value = (lccxSubDomain.value == '.lc.cx') ? true : false;
    newDomainIsURLFR.value = (lccxSubDomain.value == '.url.fr') ? true : false;
    newDomainIsSHORTDO.value = (lccxSubDomain.value == '.short.do') ? true : false;
    domain = newLCCXSubdomain.value.toLowerCase() + lccxSubDomain.value;
    confDomainSetupLoading.value = true;
  }
  else if (activeStep.value == 3 && domainOption.value == 'domainOption1') {
    activeStep.value = 4;
    return;
  }
  else if (activeStep.value == 4 && domainOption.value == 'domainOption1') {
    handleDomainLoading.value = true;
    domain = newDomainName.value;
  }

  if (domain) {
    var data = {
      'domain': domain,
      'description': newDomainDescription.value,
      'is_lccx_subdomain': newDomainIsLCCX.value,
      'is_urlfr_subdomain': newDomainIsURLFR.value,
      'is_shortdo_subdomain': newDomainIsSHORTDO.value,
      'workspaces': selectedWorkspaces.value
    }

    var request = await callAPI(DRUPAL_API_ROUTE.DOMAIN_ADD, {}, data);

    if (request.status == 200) {
      confDomainSetup.value = request.data;

      handleDomainLoading.value = false;
      if (activeStep.value == 4) addDomainDialog.value = false;

      var shortlinksDomains = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: user.value.current_workspace, type: 'shortlinks' });
      localStorage.setItem('shortlinksDomains', JSON.stringify(shortlinksDomains.data));

      var webformsDomains = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: user.value.current_workspace, type: 'webforms' });
      localStorage.setItem('webformsDomains', JSON.stringify(webformsDomains.data));
    } else {
      handleDomainLoading.value = false;
      newDomainName.value = '';
      newDomainDescription.value = '';
      newDomainIsLCCX.value = '';
      newDomainIsURLFR.value = '';
      newDomainIsSHORTDO.value = '';
      newLCCXSubdomain.value = '';
      activeStep.value = 1;
    }
  }

  if (activeStep.value == 3) activeStep.value = 4;

  if (domain) {
    newDomainName.value = '';
    newDomainDescription.value = '';
    newDomainIsLCCX.value = '';
    newDomainIsURLFR.value = '';
    newDomainIsSHORTDO.value = '';
    newLCCXSubdomain.value = '';
  }

};

const domainLookup = async () => {
  domainLookupLoading.value = true;
  domainSearchPricesResults.value = {};

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.DOMAIN_SEARCH, { domain: domainSearch.value });
    addDomainDialogHeight.value = '40rem';
    domainSearchResults.value = response.data;
  } catch (error) {
    console.log(error);
  } finally {
    domainLookupLoading.value = false;
  }

  await Promise.all(
    domainSearchResults.value.map(async (item) => {
      const [status, purchaseUrl] = await Promise.all([
        domainLookupStatus(item.domain),
        domainLookupPurchaseUrl(item.domain),
      ]);
      if (status == null) return;

      const { category, severity } = resolveStatus(status);

      domainSearchPricesResults.value[item.domain] = {
        status,
        category,
        severity,
        available: category === 'available',
        purchaseUrl,
      };
    })
  );
}

const domainLookupStatus = async (domain) => {
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.DOMAIN_SEARCH_STATUS, { domain });
    return response.data;
  } catch (error) {
    console.log(error);
  }
}

const domainLookupPurchaseUrl = async (domain) => {
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.DOMAIN_SEARCH_PRICES, { domain });
    return response.data;
  } catch (error) {
    console.log(error);
  }
}
const STATUS_CATEGORY = {
  inactive: 'available',
  undelegated: 'available',
  premium: 'premium',
  marketed: 'unavailable',
  priced: 'unavailable',
  transferable: 'unavailable',
  parked: 'unavailable',
  active: 'unavailable',
  expiring: 'unavailable',
  deleting: 'unavailable',
  disallowed: 'unavailable',
  claimed: 'unavailable',
  reserved: 'unavailable',
  dpml: 'unavailable',
  invalid: 'unavailable',
  pending: 'unknown',
  unknown: 'unknown',
};

const STATUS_PRIORITY = [
  'inactive', 'undelegated',
  'premium',
  'marketed', 'priced', 'transferable', 'parked', 'expiring', 'deleting', 'active',
  'disallowed', 'claimed', 'reserved', 'dpml', 'invalid',
  'pending', 'unknown',
];

const CATEGORY_SEVERITY = {
  available: 'success',
  premium: 'info',
  unavailable: 'danger',
  unknown: 'secondary',
};

const resolveStatus = (statusString) => {
  if (typeof statusString !== 'string') {
    return { category: 'unknown', severity: 'secondary' };
  }
  const tokens = statusString.toLowerCase().split(/\s+/).filter(Boolean);
  const keyword = STATUS_PRIORITY.find(s => tokens.includes(s)) ?? 'unknown';
  const category = STATUS_CATEGORY[keyword] ?? 'unknown';
  return { category, severity: CATEGORY_SEVERITY[category] };
}

const sortedLccxSubDomains = computed(() => {
  if (user.value.language === 'fr') {
    return [
      lccxSubDomains.value.find(item => item.value === '.url.fr'),
      ...lccxSubDomains.value.filter(item => item.value !== '.url.fr')
    ];
  }
  return lccxSubDomains.value;
});

const checkDomainStatus = async () => {
  var request = await callAPI(DRUPAL_API_ROUTE.DOMAIN, { uuid: confDomainSetup.value.id });
  var domain = request.data;
  if (domain.status == 1) {
    confDomainSetupLoading.value = false;
    confDomainSetup.value = domain;
    clearInterval(intervalId)
  }
}

watchEffect(() => {
  if (confDomainSetup.value && confDomainSetupLoading.value == true) {
    checkDomainStatus();
    clearInterval(intervalId);
    intervalId = setInterval(checkDomainStatus, 5000);
  }
})

const handleHide = () => {
  resetForm();
  activeStep.value = 1;
  domainOption.value = '';
  domainSearchResults.value = [];
  domainSearch.value = '';
  confDomainSetup.value = '';
  selectedWorkspaces.value = [];
  addDomainDialogHeight.value = '100%';
  clearInterval(intervalId);
}

const handleClose = () => {
  addDomainDialog.value = false;
  emit('domain', confDomainSetup.value)
  resetForm();
}

onUnmounted(() => {
  clearInterval(intervalId);
});

</script>

<template>
  <Dialog v-model:visible="addDomainDialog" modal :header="t('domains.add.title')" :style="{ width: '40rem' }"
    @show="fetchDomainWorkspaces" @hide="handleHide">
    <Stepper v-model:value="activeStep" linear class="basis-[50rem]" :style="{ 'height': addDomainDialogHeight }">
      <StepList>
        <Step :value="1"></Step>
        <Step :value="2"></Step>
        <Step :value="3"></Step>
        <Step :value="4"></Step>
      </StepList>
      <StepPanels>
        <StepPanel v-slot="{ activateCallback }" :value="1">
          <div class="flex flex-col">
            <span class="text-surface-500 dark:text-surface-400 block mb-5">
              {{ t('domains.add.options.title') }}
            </span>
            <div class="space-y-5">
              <div class="flex flex-col space-y-2">
                <div class="flex items-center space-x-2">
                  <RadioButton v-model="domainOption" inputId="domainOption1" name="domainOption1" value="domainOption1"
                    :disabled="!isAllowed" />
                  <label for="domainOption1">{{ t('domains.add.options.custom_domain') }} </label>
                </div>
                <div class="flex ml-5">
                  <Tag class="ms-2" severity="secondary" :value="t('domains.add.options.custom_domain_activation')"
                    rounded></Tag>
                </div>
              </div>
              <div class="flex flex-col space-y-2">
                <div class="flex items-center space-x-2">
                  <RadioButton v-model="domainOption" inputId="domainOption2" name="domainOption2" value="domainOption2"
                    :disabled="!isAllowed || !user.permissions.add_lc_cx_subdomain" />
                  <label for="domainOption2">{{ t('domains.add.options.lccx_domain') }}</label>
                </div>
                <div class="flex ml-5">
                  <Tag class="ms-2" severity="secondary" :value="t('domains.add.options.lccx_domain_activation')"
                    rounded></Tag>
                </div>
              </div>

              <div class="flex flex-col space-y-2">
                <div class="flex items-center space-x-2">
                  <RadioButton v-model="domainOption" inputId="domainOption3" name="domainOption3" value="domainOption3"
                    :disabled="!isAllowed" />
                  <label for="domainOption3">{{ t('domains.add.options.buy_domain') }}</label>
                </div>
                <div class="flex ml-5">
                  <Tag class="ms-2" severity="secondary" :value="t('domains.add.options.buy_domain_activation')"
                    rounded></Tag>
                </div>
              </div>

            </div>
          </div>
          <div class="flex pt-6 justify-between items-center w-full">
            <div class="flex-1 mr-4" v-if="!isAllowed">
              <Message icon="pi pi-info-circle" severity="info">
                {{ t('domains.add.notallowed') }}
              </Message>
            </div>
            <Button v-if="isAllowed" class="ml-auto" :label="t('domains.labels.next')" :disabled="!domainOption"
              icon="pi pi-arrow-right" iconPos="right"
              @click="currentSchemaHandle(domainOption); activateCallback(2)" />
          </div>
        </StepPanel>
        <StepPanel v-slot="{ activateCallback }" :value="2">
          <div class="flex flex-col" :class="{ 'h-48': domainOption != 'domainOption3' }">
            <div v-if="domainOption == 'domainOption1'" class="space-y-4">
              <span class="text-surface-500 dark:text-surface-400 block mb-5">
                {{ t('domains.add.custom_domain.title') }}
              </span>
              <div class="flex flex-col gap-2">
                <label for="domain" class="font-semibold">{{ t('domains.add.custom_domain.subtitle')
                  }}</label>
                <InputText v-model="newDomainName" placeholder="exemple.com, l.exemple.com" id="domain"
                  class="flex-auto" autocomplete="off" :class="{ 'p-invalid': errors.newDomainName }" />
                <Message v-if="errors.newDomainName" severity="error" size="small" variant="simple">{{
                  errors.newDomainName }}
                </Message>
              </div>
              <div class="flex flex-col gap-2">
                <label for="description" class="font-semibold"> {{ t('domains.add.custom_domain.description')
                  }}</label>
                <Textarea v-model="newDomainDescription" id="description" class="flex-auto" autocomplete="off"
                  required />
              </div>
            </div>
            <div v-if="domainOption == 'domainOption2'" class="space-y-4">
              <span class="text-surface-500 dark:text-surface-400 block">
                {{ t('domains.add.lccx_domain.title') }}
              </span>
              <div class="flex flex-col gap-2">
                <label for="domain" class="font-semibold">{{ t('domains.add.lccx_domain.subtitle') }}</label>
                <InputGroup class="sm:w-96!">
                  <InputText v-model="newLCCXSubdomain" id="domain" class="flex-auto" autocomplete="off"
                    :class="{ 'p-invalid': errors.newLCCXSubdomain }" />
                  <Select v-model="lccxSubDomain" :options="sortedLccxSubDomains" optionLabel="label"
                    optionValue="value" :placeholder="t('common.placeholders.select')"
                    :class="{ 'p-invalid': errors.lccxSubDomain }" />
                </InputGroup>
                <Message v-if="errors.newLCCXSubdomain || errors.lccxSubDomain" severity="error" size="small"
                  variant="simple">{{
                    errors.newLCCXSubdomain }}
                </Message>
              </div>
              <div class="flex flex-col gap-2">
                <label for="description" class="font-semibold">{{ t('domains.add.lccx_domain.description')
                  }}</label>
                <Textarea v-model="newDomainDescription" id="description" class="flex-auto" autocomplete="off"
                  required />
              </div>
            </div>
            <div v-if="domainOption == 'domainOption3'" class="space-y-4">
              <span class="text-surface-500 dark:text-surface-400 block mb-5">
                {{ t('domains.add.buy_domain.title') }}
              </span>
              <div class="flex flex-col gap-2">
                <label for="domain" class="font-semibold">{{ t('domains.add.buy_domain.subtitle') }}</label>
                <div class="inline-flex gap-4">
                  <InputText v-model="domainSearch" id="domain" class="flex-auto" autocomplete="off" required />
                  <Button type="button" icon="pi pi-search" v-tooltip.bottom="t('domains.add.buy_domain.search')"
                    severity="primary" @click="domainLookup" :loading="domainLookupLoading"></Button>
                </div>
                <div v-if="domainSearchResults.length > 0" class="mt-10">
                  <p class="text-right text-xs">{{ domainSearchResults.length }} {{
                    t('domains.add.buy_domain.results') }}</p>
                  <DataView :value="domainSearchResults" class="h-72">
                    <template #list="slotProps">
                      <div class="flex flex-col">
                        <div v-for="(item, index) in slotProps.items" :key="index">
                          <div class="flex flex-col sm:flex-row sm:items-center py-6 gap-4"
                            :class="{ 'border-t border-surface-200 dark:border-surface-700': index !== 0 }">
                            <div class="flex flex-col md:flex-row justify-between md:items-center flex-1 gap-6">
                              <div class="flex flex-row md:flex-col justify-between items-start gap-2">
                                <div>
                                  <div class="text-lg font-medium mt-2 flex items-center gap-2">
                                    <span>{{ item.domain }}</span>
                                    <Tag v-if="domainSearchPricesResults[item.domain]?.category"
                                      :value="t(`domains.add.buy_domain.status.${domainSearchPricesResults[item.domain].category}`)"
                                      :severity="domainSearchPricesResults[item.domain].severity" />
                                  </div>
                                </div>
                              </div>
                              <div class="flex items-center md:items-end gap-4">
                                <div class="flex items-center">
                                  <Skeleton v-if="!domainSearchPricesResults[item.domain]" size="3rem" class="mr-2" />
                                  <Button v-else as="a" :href="domainSearchPricesResults[item.domain].purchaseUrl"
                                    target="_blank" rel="noopener"
                                    v-tooltip.bottom="t('domains.add.buy_domain.purchase')" icon="pi pi-shopping-cart"
                                    :disabled="!domainSearchPricesResults[item.domain].available || !domainSearchPricesResults[item.domain].purchaseUrl" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <template #empty>
                      <span></span>
                    </template>
                  </DataView>
                </div>
              </div>
            </div>
          </div>
          <div v-if="domainSearchResults.length == 0" class="flex pt-14 w-full">
            <Button :label="t('domains.labels.previous')" severity="secondary" icon="pi pi-arrow-left"
              @click="activateCallback(1); resetForm();" :disabled="handleDomainLoading" />
            <Button v-if="domainOption != 'domainOption3'" :label="t('domains.labels.next')" icon="pi pi-arrow-right"
              :loading="handleDomainLoading" iconPos="right" @click="onSubmitCreationDomain" class="ml-auto" />
          </div>
        </StepPanel>
        <StepPanel v-slot="{ activateCallback }" :value="3">
          <div class="flex flex-col h-48 space-y-4">
            <span class="text-surface-500 dark:text-surface-400 block mb-5">
              {{ t('domains.add.workspaces.title') }}
            </span>
            <div class="flex flex-col gap-2">
              <label for="domain_workspaces" class="font-semibold">{{ t('domains.add.workspaces.subtitle') }}</label>
              <MultiSelect v-model="selectedWorkspaces" inputId="domain_workspaces" :options="domainWorkspaces"
                optionLabel="name" optionValue="id" optionDisabled="locked" display="chip" filter :loading="domainWorkspacesLoading"
                :placeholder="t('common.placeholders.select')" :showToggleAll="false" class="w-full">
                <!-- No remove icon on the default workspace. -->
                <template #chip="{ value, removeCallback }">
                  <Chip :label="domainWorkspaces.find(workspace => workspace.id == value)?.name"
                    :removable="!domainWorkspaces.find(workspace => workspace.id == value)?.locked"
                    @remove="removeCallback($event)" class="mr-1" />
                </template>
              </MultiSelect>
              <Message icon="pi pi-info-circle" severity="secondary" size="small" variant="simple">
                {{ t('domains.add.workspaces.notice') }}
              </Message>
            </div>
          </div>
          <div class="flex pt-14 w-full">
            <Button :label="t('domains.labels.previous')" severity="secondary" icon="pi pi-arrow-left"
              @click="activateCallback(2)" :disabled="handleDomainLoading" />
            <Button :label="t('domains.labels.next')" icon="pi pi-arrow-right" iconPos="right"
              :disabled="selectedWorkspaces.length == 0" :loading="handleDomainLoading"
              @click="onSubmitCreationDomain" class="ml-auto" />
          </div>
        </StepPanel>
        <StepPanel v-slot="{ activateCallback }" :value="4">
          <div class="flex flex-col">
            <div v-if="domainOption == 'domainOption1'" class="space-y-4">
              <p>{{ t('domains.add.custom_domain.configurations.content') }}</p>
              <div
                class="border-2 border-dashed border-surface-200 dark:border-surface-700 rounded bg-surface-50 dark:bg-surface-950 text-center font-medium">
                <div v-if="cloudProvider == 'AWS'">
                  <div v-if="confDomainName == '@'">
                    <p>{{ confDomainName }} A 3.33.168.1</p>
                    <p>{{ confDomainName }} A 166.117.106.149</p>
                  </div>
                  <div v-else>
                    <p>{{ confDomainName }} CNAME cname.nemorius.com</p>
                  </div>
                </div>
                <div v-else-if="cloudProvider == 'SCALEWAY'">
                  <p>{{ confDomainName }} CNAME cname.nemorius.eu</p>
                </div>
                <div v-else>
                  <p>N/A</p>
                </div>
              </div>
              <Message severity="warn">
                <p>{{ t('domains.add.custom_domain.configurations.notice1') }}</p>
              </Message>
              <p>{{ t('domains.add.custom_domain.configurations.notice2') }}</p>
            </div>
            <div v-if="domainOption == 'domainOption2'" class="flex flex-col items-center text-center space-y-16">
              <p v-if="confDomainSetupLoading" class="text-left">{{
                t('domains.add.lccx_domain.setup') }}</p>
              <p v-if="!confDomainSetupLoading" class="text-left"><i class="pi pi-check me-2"></i>{{
                t('domains.add.lccx_domain.complete') }}</p>
            </div>
            <div class="flex flex-col text-center items-center " v-if="domainOption == 'domainOption2'">
              <ProgressBar v-if="confDomainSetupLoading" mode="indeterminate" class="mt-8 h-[6px]! w-1/2">
              </ProgressBar>
            </div>
          </div>
          <div v-if="domainOption == 'domainOption1'" class="flex flex-col items-center text-center mt-8">
            <Button :label="t('domains.labels.verification')" severity="primary" @click="onSubmitCreationDomain"
              :loading="handleDomainLoading" />
          </div>
          <div v-if="domainOption == 'domainOption2'" class="pt-6 flex flex-col text-center items-center">
            <Button v-if="domainOption == 'domainOption2' && !confDomainSetupLoading" :label="t('common.labels.close')"
              severity="primary" @click="handleClose" />
          </div>
        </StepPanel>
      </StepPanels>
    </Stepper>
  </Dialog>
</template>
